import React, { useState, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import BASE_URL from '../../config/config';
import FotoPerfil from '../../components/ui/FotoPerfil';


const EditarPerfil = () => {
  const token = localStorage.getItem('AuthToken');
  const tokenInfo = token ? jwtDecode(token) : {};
  const navigate = useNavigate();

  const [showPass, setShowPass] = useState(false);
  const [showPassRep, setShowPassRep] = useState(false);
  const [nombre, setNombre] = useState(tokenInfo.nombre || '');
  const [apellidos, setApellidos] = useState(tokenInfo.apellidos || '');
  const [email, setEmail] = useState(tokenInfo.email || '');
  const [telefono, setTelefono] = useState(tokenInfo.telefono || '');
  const [contrasena, setContrasena] = useState('');
  const [repetir, setRepetir] = useState('');
  const [webs_del_cliente, setWebs_del_cliente] = useState(tokenInfo.webs_del_cliente || '');

  const errors = useMemo(() => {
    const e = {};
    if (!nombre.trim()) e.nombre = 'El nombre es obligatorio.';
    if (!apellidos.trim()) e.apellidos = 'Los apellidos son obligatorios.';
    if (!email.trim()) e.email = 'El email es obligatorio.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'El email no es válido.';
    if (telefono.trim() && !/^[0-9+()\s-]{6,}$/.test(telefono)) e.telefono = 'El teléfono no es válido.';
    if (contrasena) {
      if (contrasena.length < 6) e.contrasena = 'La contraseña debe tener al menos 6 caracteres.';
      if (contrasena !== repetir) e.repetir = 'Las contraseñas no coinciden.';
    } else if (repetir) {
      e.contrasena = 'Introduce primero la contraseña.';
    }
    return e;
  }, [nombre, apellidos, email, telefono, contrasena, repetir]);

  const isValid = Object.keys(errors).length === 0;

  const handleLogout = () => {
    localStorage.removeItem('AuthToken');
    navigate('/iniciar-sesion', { replace: true });
    window.location.reload();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveProfile = async () => {
    if (!isValid) {
      toast.error('Revisa los campos marcados antes de guardar.');
      return;
    }
    // La contraseña viaja en claro como 'nueva_contrasena': el hasheo lo hace
    // el servidor, igual que al crear un usuario o al resetearla.
    const updatedData = {
      nombre,
      apellidos,
      email,
      telefono,
      ...(contrasena && { nueva_contrasena: contrasena }),
      webs_del_cliente,
    };
    try {
      const response = await axios.put(
        `${BASE_URL}/usuario/update/${tokenInfo.usuario}`,
        updatedData,
        { headers: { Authorization: token } }
      );
      if (response.status === 200) {
        toast.success('Perfil actualizado con éxito.');
        if (contrasena) {
          handleLogout();
        } else if (response.data?.token) {
          localStorage.setItem('AuthToken', response.data.token);
          window.dispatchEvent(new Event('auth-token-changed'));
        }
      } else {
        toast.error('Hubo un problema al actualizar el perfil.');
      }
    } catch (error) {
      console.error('Error al actualizar el perfil:', error);
      toast.error('Ocurrió un error al actualizar el perfil.');
    }
  };

  return (
    <main className="site-width edit-profile-page">
      <button className="boton-volver" onClick={() => navigate('/dashboard')}>
        ← Volver
      </button>

      <div className="edit-profile-card">
        <div className="edit-profile-header">
          <div className="edit-profile-avatar">
            <FotoPerfil />
          </div>
          <div className="edit-profile-heading">
            <h2>Edita tu perfil</h2>
            <p className="edit-profile-username">@{tokenInfo.usuario}</p>
            <p className="edit-profile-hint">Actualiza tus datos personales y de acceso. Pulsa sobre la foto para cambiarla.</p>
          </div>
        </div>

        <div className="edit-profile-section">
          <div className="texto-edit-profile">
            <h3>Información personal</h3>
            <p>Esta información sirve para dirigirnos hacia ti y contactar contigo si fuera necesario.</p>
          </div>

          <div className="edit-profile-grid">
            <div className="campo-ajuste">
              <label htmlFor="nombre">Nombre</label>
              <input type="text" id="nombre" value={nombre} onChange={e => setNombre(e.target.value)} className={errors.nombre ? 'input-error' : ''} />
              {errors.nombre && <p className="campo-error">{errors.nombre}</p>}
            </div>
            <div className="campo-ajuste">
              <label htmlFor="apellidos">Apellidos</label>
              <input type="text" id="apellidos" value={apellidos} onChange={e => setApellidos(e.target.value)} className={errors.apellidos ? 'input-error' : ''} />
              {errors.apellidos && <p className="campo-error">{errors.apellidos}</p>}
            </div>
            <div className="campo-ajuste">
              <label htmlFor="email">Email</label>
              <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className={errors.email ? 'input-error' : ''} />
              {errors.email && <p className="campo-error">{errors.email}</p>}
            </div>
            <div className="campo-ajuste">
              <label htmlFor="telefono">Teléfono</label>
              <input type="text" id="telefono" value={telefono} onChange={e => setTelefono(e.target.value)} className={errors.telefono ? 'input-error' : ''} />
              {errors.telefono && <p className="campo-error">{errors.telefono}</p>}
            </div>
            <div className="campo-ajuste edit-profile-full">
              <label htmlFor="webs_del_cliente">Sitios en mantenimiento</label>
              <p>En caso de ser más de uno, separa los dominios con comas.</p>
              <input type="text" id="webs_del_cliente" value={webs_del_cliente} onChange={e => setWebs_del_cliente(e.target.value)} />
            </div>
          </div>
        </div>

        <hr className="edit-profile-divider" />

        <div className="edit-profile-section">
          <div className="texto-edit-profile">
            <h3>Usuario y seguridad</h3>
            <p>Tu usuario no se puede modificar. Cambia la contraseña de acceso cuando lo necesites.</p>
          </div>

          <div className="campo-ajuste edit-profile-full">
            <label htmlFor="usuario">Usuario</label>
            <input type="text" name="usuario" id="usuario" value={`@${tokenInfo.usuario}`} disabled />
            <p>¿Problemas con tu usuario? Llámanos al <a href="tel:+34856901276">856 901 276</a> ext. #2.</p>
          </div>

          <div className="edit-profile-grid">
            <div className="campo-ajuste">
              <label htmlFor="contrasena">Contraseña</label>
              <div className={`input-with-toggle ${errors.contrasena ? 'input-error' : ''}`}>
                <input
                  type={showPass ? 'text' : 'password'}
                  id="contrasena"
                  value={contrasena}
                  onChange={e => setContrasena(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Dejar vacío para mantener la actual"
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowPass(v => !v)}
                  aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  aria-pressed={showPass}
                  title={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPass ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {errors.contrasena && <p className="campo-error">{errors.contrasena}</p>}
            </div>

            <div className="campo-ajuste">
              <label htmlFor="repetir-contrasena">Repetir contraseña</label>
              <div className={`input-with-toggle ${errors.repetir ? 'input-error' : ''}`}>
                <input
                  type={showPassRep ? 'text' : 'password'}
                  id="repetir-contrasena"
                  value={repetir}
                  onChange={e => setRepetir(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Repite la nueva contraseña"
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowPassRep(v => !v)}
                  aria-label={showPassRep ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  aria-pressed={showPassRep}
                  title={showPassRep ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassRep ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {errors.repetir && <p className="campo-error">{errors.repetir}</p>}
            </div>
          </div>
        </div>

        <div className="adhoc-warning">
          <p>Si cambias la contraseña, la sesión se cerrará al guardar. Si la dejas vacía, no se modificará y la sesión seguirá activa.</p>
        </div>

        <div className="edit-profile-actions">
          <button className="boton-primario" onClick={handleSaveProfile} disabled={!isValid}>Guardar perfil</button>
        </div>
      </div>
      <ToastContainer position="bottom-right" autoClose={3000} />
    </main>
  );
};

export default EditarPerfil;
